import { useMemo, useState, useEffect } from "react";
import styles from "./PriceFooter.module.scss";
import {
  NewButton,
  NewButtonSize,
  Skeleton,
  StatusBadge,
  StatusBadgeVariant,
} from "@bale/ui";
import Footer from "components/commons/footer/Footer";
import { strings } from "intl/fa";
import NumToPersian from "utils/numToPersian";
import { digitsToHindi, formatStrings, toMoneyFormat } from "@bale/utils";
import { useCalculatedPrice } from "hooks/useCalculatedPrice";
import { AdType } from "core/orderAd/orderAd.slice";
import { AdSpot, ApiAdRecord } from "core/orderAd/orderAd.entity";
import { useAppSelector } from "utils/store";
import { useCampaignPrice } from "hooks/useCampaignPrice";

interface Props {
  button: {
    text: string;
    disabled?: boolean;
    onClick: () => void;
    loading: boolean;
  };
  isCampaign?: boolean;
  viewCount?: number;
  clickCount?: number;
  showPrice?: boolean;
}

const PriceFooter = ({
  button,
  isCampaign = false,
  viewCount,
  clickCount,
  showPrice = true,
}: Props) => {
  const orderAdInfo = useAppSelector(
    (state) => state.orderAd.info
  ) as ApiAdRecord & {
    adType?: AdType;
  };
  const isView = orderAdInfo.adType === AdType.view;

  const viewNumber = useMemo(() => {
    return viewCount ?? orderAdInfo.view_count;
  }, [orderAdInfo, viewCount]);

  const clickNumber = useMemo(() => {
    return clickCount ?? orderAdInfo.click_count;
  }, [orderAdInfo, clickCount]);

  const selectedTargetType = useAppSelector(
    (state) => state.orderAd.info.target_type
  );
  const isInstant = useAppSelector((state) => state.orderAd.info.is_instant);
  const { loading, finalPrice, finalBasePrice, totalPrice } =
    useCalculatedPrice({
      viewCount: viewNumber ?? 0,
      clickCount: clickNumber ?? 0,
      spot: AdSpot.AdSpotDialog,
      link: orderAdInfo.link,
      is_instant: isInstant,
      target_type: selectedTargetType,
    });

  const showBadge = useMemo(() => {
    return totalPrice / finalPrice > 1;
  }, [totalPrice, finalPrice]);

  const campaignBasePrice = useCampaignPrice({
    description: orderAdInfo.link,
    adSpot: orderAdInfo.spot,
    adType: isView ? AdType.view : AdType.click,
    target_type: selectedTargetType,
  });

  const price = useMemo(() => {
    if (!isCampaign) {
      return finalPrice;
    } else {
      return campaignBasePrice;
    }
  }, [isCampaign, finalPrice, campaignBasePrice]);

  const [displayedPrice, setDisplayedPrice] = useState(price);

  useEffect(() => {
    if (loading || price <= 0) return;
    let start = displayedPrice;
    const end = price;
    const duration = 500;
    const largeDifferenceThreshold = 100;
    const stepTime = Math.abs(Math.floor(duration / (end - start)));
    let current = start;
    const increment = end > start ? 10 : -10;

    if (Math.abs(end - start) > largeDifferenceThreshold) {
      current = end - increment * largeDifferenceThreshold;
      setDisplayedPrice(current);
    }

    const timer = setInterval(() => {
      current += increment;
      if (
        (increment > 0 && current >= end) ||
        (increment < 0 && current <= end)
      ) {
        clearInterval(timer);
        setDisplayedPrice(end);
      } else {
        setDisplayedPrice(current);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [price, loading]);

  // const [footerText, footerPercentage] = useMemo(() => {
  //   const options: string[] = [];
  //   let percentage = 0;
  //   if (isInstant) {
  //     options.push(strings.dialog_ad_form.publish_step.instant_ad);
  //     percentage += GeneralConfig.InstantPrice;
  //   }

  //   if (selectedTargetType === targetType.GENDER) {
  //     options.push(
  //       strings.dialog_ad_form.more_settings_step.gender_target.rawTitle
  //     );
  //     percentage += GeneralConfig.GenderPrice;
  //   }

  //   return [options.length > 0 ? options.join(" و ") : undefined, percentage];
  // }, [isInstant, selectedTargetType]);

  return (
    <Footer className={styles.footer}>
      {(viewNumber > 0 || clickNumber > 0) && showPrice && (
        <>
          <div className={styles.price}>
            <div className={styles.title}>
              <p>{strings.common.price}:</p>
              {showBadge && (
                <StatusBadge
                  variant={StatusBadgeVariant.LARGE}
                  text={formatStrings(strings.priceFooter.discount_badge)}
                />
              )}
            </div>

            <p className={styles.priceValue}>
              {loading && !isCampaign ? (
                <Skeleton type="text" width="100px" height="20px" />
              ) : (
                digitsToHindi(toMoneyFormat(String(displayedPrice))) +
                " " +
                strings.common.rial
              )}
            </p>
          </div>
          <p className={styles.priceAsText} dir="ltr">
            {loading && !isCampaign ? (
              <Skeleton type="text" width="200px" height="20px" />
            ) : (
              NumToPersian(price / 10) + " " + strings.common.toman
            )}
          </p>
          {/* {footerText && (
            <div className={styles.footerText}>
              <Alert
                size={20}
                color={getColorByName(ThemeColors.color_alert)}
                className={styles.icon}
              />
              <p data-testid="priceFooterText">
                {formatStrings(strings.priceFooter.text, {
                  ["text"]: footerText,
                  ["percentage"]: digitsToHindi(String(footerPercentage)),
                })}
              </p>
            </div>
          )} */}
        </>
      )}
      <NewButton
        style={{ width: "100%", maxWidth: 412 }}
        size={NewButtonSize.LARGE}
        {...button}
      />
    </Footer>
  );
};

export default PriceFooter;
